import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchPointRisks, fetchRisks } from "./api";
import {
  rankPerils,
  yearsSinceLast,
  type AreaRisks,
  type PerilRow,
  type PointRisks,
} from "./risks";
import type { PriceLevel } from "../map/levels";

export interface AreaRisksResult {
  isLoading: boolean;
  isError: boolean;
  data: AreaRisks | null;
  /** Declared perils, worst first. Empty when nothing has ever been declared. */
  perils: PerilRow[];
  /** Years since the most recent declaration, or null if there never was one. */
  quietFor: number | null;
}

/**
 * One area's hazard record.
 *
 * `staleTime: Infinity` because none of this moves inside a session — CatNat
 * declarations are added a few times a year nationally, and radon and seismic
 * zoning move once a decade.
 */
export function useAreaRisks(
  area: { scope: PriceLevel; code: string } | null,
): AreaRisksResult {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["risks", area?.scope, area?.code],
    queryFn: () => fetchRisks(area!.scope, area!.code),
    enabled: area !== null,
    staleTime: Infinity,
    retry: false,
  });

  const perils = useMemo(() => (data ? rankPerils(data.summary) : []), [data]);
  const quietFor = useMemo(() => (data ? yearsSinceLast(data.summary) : null), [data]);

  return {
    // react-query reports isLoading for a disabled query, which would render
    // "no selection" as "loading" forever.
    isLoading: area !== null && isLoading,
    isError,
    data: data ?? null,
    perils,
    quietFor,
  };
}

export interface PointRisksResult {
  isLoading: boolean;
  isError: boolean;
  data: PointRisks | null;
}

/**
 * The exact point: clay class from local data, nearby inventories from
 * Géorisques.
 *
 * The radius is in the key because it is part of the question. Coordinates are
 * rounded there too, so a click one float apart from the last does not
 * fragment the cache.
 */
export function usePointRisks(
  point: { lon: number; lat: number } | null,
  radius: number,
): PointRisksResult {
  const { data, isLoading, isError } = useQuery({
    queryKey: [
      "risks",
      "point",
      point?.lon.toFixed(5),
      point?.lat.toFixed(5),
      radius,
    ],
    queryFn: () => fetchPointRisks(point!.lon, point!.lat, radius),
    enabled: point !== null,
    staleTime: Infinity,
    retry: false,
  });

  return {
    isLoading: point !== null && isLoading,
    isError,
    data: data ?? null,
  };
}
